import { useCallback, useEffect, useState } from "react";
import { LayoutGrid, List, Phone, Mail, IndianRupee } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api, errMsg, inr, dt, refLabel } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import EntityPage, { useRefs } from "@/components/EntityPage";
import EntityForm from "@/components/EntityForm";
import { PageHeader, ProjectSelect, Badge2 } from "@/components/common";

const COL = { New: "border-t-slate-400", Contacted: "border-t-cyan-500", Interested: "border-t-blue-500", "Site Visit": "border-t-violet-500", Negotiation: "border-t-amber-500", Booked: "border-t-emerald-500", Lost: "border-t-rose-500" };

function LeadSheet({ lead, onClose, refs }) {
  const { can } = useAuth();
  const [acts, setActs] = useState([]);
  const [note, setNote] = useState("");
  const load = useCallback(() => api.get(`/leads/${lead.id}/activities`).then((r) => setActs(r.data)), [lead.id]);
  useEffect(() => { load(); }, [load]);
  const add = async () => { try { await api.post(`/leads/${lead.id}/activities`, { type: "note", note }); setNote(""); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="bg-white border-slate-800 w-full sm:max-w-md overflow-y-auto" data-testid="lead-detail-sheet">
        <SheetHeader><SheetTitle>{lead.name}</SheetTitle></SheetHeader>
        <div className="space-y-2 text-sm mt-4">
          <div className="flex gap-2 items-center"><Badge2>{lead.stage}</Badge2><Badge2 tone="cyan">{lead.source}</Badge2><span className="font-mono text-xs text-slate-400">score {lead.score ?? "-"}</span></div>
          <div className="flex items-center gap-2 text-slate-300"><Phone className="h-4 w-4" />{lead.phone || "-"}</div>
          <div className="flex items-center gap-2 text-slate-300"><Mail className="h-4 w-4" />{lead.email || "-"}</div>
          <div className="flex items-center gap-2 text-slate-300"><IndianRupee className="h-4 w-4" />{inr(lead.budget)} · {lead.configuration}</div>
          <div className="text-slate-400">Project: {refLabel((refs.projects || []).find((p) => p.id === lead.project_id))}</div>
          <div className="text-slate-400">Owner: {refLabel((refs.users || []).find((u) => u.id === lead.assigned_to))}</div>
        </div>
        <div className="mt-6 font-semibold text-sm">Activity timeline</div>
        {can("sales", "write") && <div className="flex gap-2 mt-2"><Input placeholder="Log a call or note..." value={note} onChange={(e) => setNote(e.target.value)} data-testid="lead-activity-input" /><Button onClick={add} disabled={!note} data-testid="lead-activity-add-button">Add</Button></div>}
        <div className="mt-4 space-y-3 border-l border-slate-800 pl-4" data-testid="lead-activity-list">
          {acts.map((a) => <div key={a.id}><div className="text-sm">{a.note}</div><div className="text-[11px] text-slate-500 font-mono">{a.user_name} · {dt(a.created_at)}</div></div>)}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Kanban() {
  const { schema, can } = useAuth();
  const cfg = schema.entities.leads;
  const refs = useRefs(cfg.fields);
  const [leads, setLeads] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [q, setQ] = useState("");
  const [drag, setDrag] = useState(null);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState(false);
  const load = useCallback(() => api.get("/leads", { params: projectId ? { project_id: projectId } : {} }).then((r) => setLeads(r.data)), [projectId]);
  useEffect(() => { load(); }, [load]);
  const move = async (stage) => {
    const l = leads.find((x) => x.id === drag);
    setDrag(null);
    if (!l || l.stage === stage) return;
    setLeads((ls) => ls.map((x) => (x.id === l.id ? { ...x, stage } : x)));
    try { await api.put(`/leads/${l.id}`, { stage }); toast.success(`${l.name} → ${stage}`); } catch (e) { toast.error(errMsg(e)); load(); }
  };
  const shown = leads.filter((l) => !q || l.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <Input placeholder="Search leads..." value={q} onChange={(e) => setQ(e.target.value)} className="w-56" data-testid="kanban-search-input" />
        <ProjectSelect allowAll value={projectId} onChange={setProjectId} testid="kanban-project-filter" />
        {can("sales", "write") && <Button className="ml-auto" onClick={() => setForm(true)} data-testid="kanban-create-lead-button">New Lead</Button>}
      </div>
      <div className="flex gap-4 overflow-x-auto pb-4 custom-scrollbar" data-testid="leads-kanban">
        {schema.lead_stages.map((s) => {
          const items = shown.filter((l) => l.stage === s);
          return (
            <div key={s} onDragOver={(e) => e.preventDefault()} onDrop={() => move(s)} data-testid={`kanban-column-${s.replace(/\s/g, "-").toLowerCase()}`}
              className={`w-72 shrink-0 rounded-xl bg-slate-900/50 border border-slate-800 border-t-2 ${COL[s]} flex flex-col max-h-[70vh]`}>
              <div className="px-4 py-3 flex justify-between items-center"><span className="text-sm font-semibold">{s}</span><span className="text-xs font-mono text-slate-500">{items.length} · {inr(items.reduce((a, b) => a + (b.budget || 0), 0))}</span></div>
              <div className="px-3 pb-3 space-y-2 overflow-y-auto custom-scrollbar flex-1">
                {items.map((l) => (
                  <div key={l.id} draggable={can("sales", "write")} onDragStart={() => setDrag(l.id)} onClick={() => setSel(l)} data-testid={`kanban-card-${l.id}`}
                    className="rounded-lg bg-white shadow-sm border border-slate-800 p-3 cursor-grab active:cursor-grabbing hover:border-slate-600 transition-colors">
                    <div className="flex justify-between gap-2"><span className="text-sm font-semibold truncate">{l.name}</span><span className="text-[10px] font-mono text-emerald-400">{l.score}</span></div>
                    <div className="text-xs text-slate-500 mt-1 truncate">{refLabel((refs.projects || []).find((p) => p.id === l.project_id))}</div>
                    <div className="flex justify-between mt-2 text-[11px]"><Badge2 tone="cyan">{l.source}</Badge2><span className="font-mono text-slate-400">{inr(l.budget)}</span></div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {sel && <LeadSheet lead={sel} refs={refs} onClose={() => setSel(null)} />}
      <EntityForm entity="leads" refs={refs} open={form} onClose={() => setForm(false)} defaults={{ stage: "New" }} onSaved={load} />
    </div>
  );
}

export default function Leads() {
  const [view, setView] = useState("kanban");
  return (
    <div data-testid="leads-page">
      <PageHeader label="Sell" title="Lead Management" subtitle="Drag leads across New → Contacted → Interested → Site Visit → Negotiation → Booked → Lost.">
        <Button variant={view === "kanban" ? "default" : "secondary"} onClick={() => setView("kanban")} data-testid="leads-view-kanban"><LayoutGrid className="h-4 w-4 mr-1" />Kanban</Button>
        <Button variant={view === "table" ? "default" : "secondary"} onClick={() => setView("table")} data-testid="leads-view-table"><List className="h-4 w-4 mr-1" />Table</Button>
      </PageHeader>
      {view === "kanban" ? <Kanban /> : <EntityPage entity="leads" defaults={{ stage: "New" }} />}
    </div>
  );
}
